export type ScriptJobStatusValue = 'queued' | 'processing' | 'stage_1' | 'stage_2' | 'partial' | 'repairing' | 'completed' | 'failed';

type JsonRecord = Record<string, unknown>;

export interface ScriptJobStatus {
    orgId?: string;
    projectId?: string;
    status: ScriptJobStatusValue;
    progress: string;
    pipeline?: 'metadata-only' | 'ocr';
    error?: string;
    resultPath?: string;
    blockCount?: number;
    actCount?: number;
    sceneCount?: number;
    failedBatches?: Array<{ batchNum: number; startPage: number; endPage: number; error: string }>;
    phaseErrors?: Record<string, string>;
    persistedProductionElements?: {
        props?: number;
        soundCues?: number;
        persistedAt?: string;
    };
    productionElementPersistence?: {
        status?: string;
    };
}

export type ScriptJobWithId = ScriptJobStatus & { jobId: string };

const isRecord = (value: unknown): value is JsonRecord => (
    value !== null && typeof value === 'object' && !Array.isArray(value)
);

const asString = (value: unknown, fallback = ''): string => (
    typeof value === 'string' ? value : fallback
);

const asOptionalString = (value: unknown): string | undefined => (
    typeof value === 'string' ? value : undefined
);

const asOptionalNumber = (value: unknown): number | undefined => (
    typeof value === 'number' ? value : undefined
);

const toScriptJobStatusValue = (value: unknown): ScriptJobStatusValue => (
    typeof value === 'string' ? value as ScriptJobStatusValue : 'queued'
);

const toPipeline = (value: unknown): ScriptJobStatus['pipeline'] => (
    value === 'metadata-only' || value === 'ocr' ? value : undefined
);

const toFailedBatches = (value: unknown): ScriptJobStatus['failedBatches'] => (
    Array.isArray(value) ? value as NonNullable<ScriptJobStatus['failedBatches']> : undefined
);

const toPhaseErrors = (value: unknown): ScriptJobStatus['phaseErrors'] => {
    if (!isRecord(value)) return;

    const entries = Object.entries(value).filter((entry): entry is [string, string] => typeof entry[1] === 'string');
    return Object.fromEntries(entries);
};

const toPersistedProductionElements = (value: unknown): ScriptJobStatus['persistedProductionElements'] => {
    if (!isRecord(value)) return;

    return {
        props: asOptionalNumber(value.props),
        soundCues: asOptionalNumber(value.soundCues),
        persistedAt: asOptionalString(value.persistedAt),
    };
};

const toProductionElementPersistence = (value: unknown): ScriptJobStatus['productionElementPersistence'] => {
    if (!isRecord(value)) return;

    return {
        status: asOptionalString(value.status),
    };
};

export function toScriptJobStatus(value: unknown): ScriptJobStatus {
    const data = isRecord(value) ? value : {};

    return {
        orgId: asOptionalString(data.orgId),
        projectId: asOptionalString(data.projectId),
        status: toScriptJobStatusValue(data.status),
        progress: asString(data.progress),
        pipeline: toPipeline(data.pipeline),
        error: asOptionalString(data.error),
        resultPath: asOptionalString(data.resultPath),
        blockCount: asOptionalNumber(data.blockCount),
        actCount: asOptionalNumber(data.actCount),
        sceneCount: asOptionalNumber(data.sceneCount),
        failedBatches: toFailedBatches(data.failedBatches),
        phaseErrors: toPhaseErrors(data.phaseErrors),
        persistedProductionElements: toPersistedProductionElements(data.persistedProductionElements),
        productionElementPersistence: toProductionElementPersistence(data.productionElementPersistence),
    };
}

export function getFirstScriptJobFromSnapshot(snapshot: {
    empty?: boolean;
    docs: Array<{ id: string; data: () => unknown }>;
}): ScriptJobWithId | null {
    if (snapshot.empty) {
        return null;
    }

    const docSnap = snapshot.docs[0];
    if (!docSnap) {
        return null;
    }

    return {
        jobId: docSnap.id,
        ...toScriptJobStatus(docSnap.data()),
    };
}
